import { Mark, Range, mergeAttributes } from '@tiptap/core'
import { Node, Mark as PMMark } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'

import type { UUID } from '@/types/Id'

export const SET_ACTIVE_THREAD_META_KEY = 'setActiveThread'
export const UNSET_ACTIVE_THREAD_META_KEY = 'unsetActiveThread'
export const THREAD_MOUSE_OVER_META_KEY = 'threadMouseOver'
export const THREAD_MOUSE_OUT_META_KEY = 'threadMouseOut'

interface CommentClasses {
  thread: string
  threadHovered: string
  threadActive: string
  threadResolved: string
  threadNeedsReview: string
  threadNeedsReviewHovered: string
  threadNeedsReviewActive: string
}

export interface MarkWithRange {
  mark: PMMark
  range: Range
}

export interface CommentOptions {
  HTMLAttributes: Record<string, any>
  classes: CommentClasses
  onActiveChange?: (id: UUID | null) => void
}

export interface CommentPluginState {
  activeThreadId: UUID | null
  hoveredThreadId: UUID | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      selectComment: (attributes: { id: UUID }) => ReturnType
      setComment: (attributes: { id: UUID }) => ReturnType
      removeAllCommentsNotIn: (attributes: { ids: UUID[] }) => ReturnType
      removeComment: (attributes: { id: UUID }) => ReturnType
      removeComments: (attributes: { ids: UUID[] }) => ReturnType
      resolveComment: (attributes: { id: UUID }) => ReturnType
      unresolveComment: (attributes: { id: UUID }) => ReturnType
      unresolveComments: (attributes: { ids: UUID[] }) => ReturnType
    }
  }
}

const getDefaultThreadClass = (
  resolved: boolean,
  needsReview: boolean,
  classes: CommentClasses,
) => {
  // resolved takes precedence over needsReview
  if (resolved) {
    return classes.threadResolved
  }
  if (needsReview) {
    return classes.threadNeedsReview
  }
  return classes.thread
}

export const Comment = Mark.create<CommentOptions>({
  name: 'comment',

  // allow comments to stack on top of each other
  excludes: '',

  addOptions() {
    return {
      HTMLAttributes: {},
      classes: {
        thread: 'bg-yellow-100 dark:bg-yellow-700',
        threadHovered: 'bg-yellow-300 dark:bg-yellow-600',
        threadActive: 'bg-yellow-300 dark:bg-yellow-600',
        threadResolved: '',
        threadNeedsReview:
          'bg-red-200 dark:bg-red-800 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500',
        threadNeedsReviewHovered:
          'bg-red-200 dark:bg-red-800 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500',
        threadNeedsReviewActive:
          'bg-red-200 dark:bg-red-800 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500',

        // TODO: add these back when we have hallucination comments as part of the needs review process
        // threadNeedsReviewHovered:
        //   'bg-red-300 dark:bg-red-600/60 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500',
        // threadNeedsReviewActive:
        //   'bg-red-300 dark:bg-red-600/60 border-b border-dashed dark:text-inherit border-red-400 dark:border-red-500',
      },
      onActiveChange: () => {},
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes) => {
          return {
            'data-comment-id': attributes.id,
          }
        },
      },
      resolved: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-comment-resolved') === 'true',
        renderHTML: (attributes) => {
          return {
            'data-comment-resolved': attributes.resolved,
            class: getDefaultThreadClass(
              attributes.resolved,
              attributes.needsReview,
              this.options.classes,
            ),
          }
        },
      },
      needsReview: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-comment-needs-review') === 'true',
        renderHTML: (attributes) => {
          return {
            'data-comment-needs-review': attributes.needsReview,
            class: getDefaultThreadClass(
              attributes.resolved,
              attributes.needsReview,
              this.options.classes,
            ),
          }
        },
      },
      isSystemThread: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-comment-is-system-thread') === 'true',
        renderHTML: (attributes) => {
          return {
            'data-comment-is-system-thread': attributes.isSystemThread,
          }
        },
      },
    }
  },

  addCommands() {
    return {
      removeComment:
        ({ id }) =>
        ({ tr, dispatch }) => {
          if (!id) return false

          const commentMarksWithRange: MarkWithRange[] = []

          tr.doc.descendants((node, pos) => {
            const commentMark = node.marks.find(
              (mark) => mark.type.name === 'comment' && mark.attrs.id === id,
            )

            if (!commentMark) return

            commentMarksWithRange.push({
              mark: commentMark,
              range: {
                from: pos,
                to: pos + node.nodeSize,
              },
            })
          })

          commentMarksWithRange.forEach(({ mark, range }) => {
            tr.removeMark(range.from, range.to, mark)
          })

          dispatch?.(tr)
          return true
        },
      removeComments:
        ({ ids }) =>
        ({ tr, dispatch }) => {
          if (!ids.length) return false

          const commentMarksWithRange: MarkWithRange[] = []

          tr.doc.descendants((node, pos) => {
            const commentMark = node.marks.find(
              (mark) =>
                mark.type.name === 'comment' && ids.includes(mark.attrs.id),
            )

            if (!commentMark) return

            commentMarksWithRange.push({
              mark: commentMark,
              range: {
                from: pos,
                to: pos + node.nodeSize,
              },
            })
          })

          commentMarksWithRange.forEach(({ mark, range }) => {
            tr.removeMark(range.from, range.to, mark)
          })

          dispatch?.(tr)
          return true
        },
      removeAllCommentsNotIn:
        ({ ids }) =>
        ({ tr, dispatch }) => {
          // NOTE: this is a dangerous function, proceed with caution
          if (!ids.length) return false

          const commentMarksWithRange: MarkWithRange[] = []

          tr.doc.descendants((node, pos) => {
            const commentMark = node.marks.find(
              (mark) =>
                // only remove comments that are not in the ids list
                mark.type.name === 'comment' && !ids.includes(mark.attrs.id),
            )

            if (!commentMark) return

            commentMarksWithRange.push({
              mark: commentMark,
              range: {
                from: pos,
                to: pos + node.nodeSize,
              },
            })
          })

          commentMarksWithRange.forEach(({ mark, range }) => {
            tr.removeMark(range.from, range.to, mark)
          })

          dispatch?.(tr)
          return true
        },
      resolveComment:
        (attributes) =>
        ({ tr, state }) => {
          const { doc } = state
          let updated = false

          doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.id === attributes.id
              ) {
                // since we allow comments to stack on top of each other, we need to remove the mark
                // before adding it back with the resolved status set to true
                tr.removeMark(pos, pos + node.nodeSize, mark)
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  state.schema.marks[this.name].create({
                    ...mark.attrs,
                    resolved: true,
                  }),
                )
                updated = true
              }
            })
          })

          return updated
        },
      selectComment:
        (attributes) =>
        ({ state, commands }) => {
          const { doc } = state
          let found = false

          doc.descendants((node, pos) => {
            if (found) return false

            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.id === attributes.id
              ) {
                commands.setTextSelection({
                  from: pos,
                  to: pos + node.nodeSize,
                })
                this.storage.activeThreadId = attributes.id
                found = true
              }
            })
          })

          return found
        },
      setComment:
        (attributes) =>
        ({ state, tr, dispatch }) => {
          tr = state.tr
            .setSelection(
              TextSelection.create(
                state.doc,
                state.selection.from,
                state.selection.to,
              ),
            )
            .addMark(
              state.selection.from,
              state.selection.to,
              this.editor.schema.marks.comment.create({ id: attributes.id }),
            )
          dispatch?.(tr)
          return true
        },
      unresolveComment:
        (attributes) =>
        ({ tr, state }) => {
          const { doc } = state
          let updated = false

          doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.id === attributes.id
              ) {
                // since we allow comments to stack on top of each other, we need to remove the mark
                // before adding it back with the resolved status set to false
                tr.removeMark(pos, pos + node.nodeSize, mark)
                tr.addMark(
                  pos,
                  pos + node.nodeSize,
                  state.schema.marks[this.name].create({
                    ...mark.attrs,
                    resolved: false,
                  }),
                )
                updated = true
              }
            })
          })

          return updated
        },
      unresolveComments:
        (attributes) =>
        ({ editor, tr, state }) => {
          const { doc } = state
          let command = editor.chain()
          attributes.ids.forEach((id) => {
            command = command.unresolveComment({ id })
          })
          return command.run()
        },
    }
  },

  addProseMirrorPlugins() {
    const key = new PluginKey('comment')
    const extension = this
    return [
      new Plugin<CommentPluginState>({
        key,
        state: {
          init() {
            return {
              activeThreadId: null,
              hoveredThreadId: null,
            }
          },
          apply(tr, value) {
            const activeThreadId: UUID | undefined = tr.getMeta(
              SET_ACTIVE_THREAD_META_KEY,
            )
            const unsetActiveThread: boolean | undefined = tr.getMeta(
              UNSET_ACTIVE_THREAD_META_KEY,
            )
            const threadMouseOverId = tr.getMeta(THREAD_MOUSE_OVER_META_KEY)
            const threadMouseOutId = tr.getMeta(THREAD_MOUSE_OUT_META_KEY)

            let newState = value
            if (threadMouseOverId !== undefined) {
              newState = {
                ...newState,
                hoveredThreadId: threadMouseOverId,
              }
            }

            if (
              threadMouseOutId !== undefined &&
              value.hoveredThreadId === threadMouseOutId
            ) {
              newState = {
                ...newState,
                hoveredThreadId: null,
              }
            }

            if (activeThreadId) {
              extension.options.onActiveChange?.(activeThreadId)
              newState = {
                ...newState,
                activeThreadId,
              }
            }

            if (unsetActiveThread) {
              extension.options.onActiveChange?.(null)
              newState = {
                ...newState,
                activeThreadId: null,
              }
            }

            return newState
          },
        },
        props: {
          decorations: (state) => {
            const pluginState = key.getState(state)
            if (!pluginState) {
              return null
            }

            if (!pluginState.hoveredThreadId && !pluginState.activeThreadId) {
              return null
            }

            const decorations: Decoration[] = []

            state.doc.descendants((node, pos) => {
              node.marks.forEach((mark) => {
                // don't apply any decorations if the mark is not a comment or if it doesn't have an id
                if (
                  mark.type.name !== 'comment' ||
                  mark.attrs.id === undefined ||
                  mark.attrs.id === null
                ) {
                  return
                }
                if (mark.attrs.resolved === true) {
                  decorations.push(
                    Decoration.inline(pos, pos + node.nodeSize, {
                      class: this.options.classes.threadResolved,
                    }),
                  )
                } else if (mark.attrs.id === pluginState.hoveredThreadId) {
                  if (mark.attrs.needsReview) {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.threadNeedsReviewHovered,
                      }),
                    )
                  } else {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.threadHovered,
                      }),
                    )
                  }
                } else if (mark.attrs.id === pluginState.activeThreadId) {
                  if (mark.attrs.needsReview) {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.threadNeedsReviewActive,
                      }),
                    )
                  } else {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.threadActive,
                      }),
                    )
                  }
                } else {
                  if (mark.attrs.needsReview) {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.threadNeedsReview,
                      }),
                    )
                  } else {
                    decorations.push(
                      Decoration.inline(pos, pos + node.nodeSize, {
                        class: this.options.classes.thread,
                      }),
                    )
                  }
                }
              })
            })

            return DecorationSet.create(state.doc, decorations)
          },

          handleClick: (view: EditorView, pos: number, event: MouseEvent) => {
            const target = event.target as HTMLElement
            const threadId = target.getAttribute('data-comment-id')
            const tr = view.state.tr
            if (threadId) {
              tr.setMeta(SET_ACTIVE_THREAD_META_KEY, threadId)
              view.dispatch(tr)
              return true
            } else {
              const parent = target.parentElement
              if (parent && !parent.getAttribute('data-comment-id')) {
                tr.setMeta(UNSET_ACTIVE_THREAD_META_KEY, true)
                view.dispatch(tr)
              }
            }
            return false
          },
        },
      }),
    ]
  },

  onSelectionUpdate() {
    const { $from } = this.editor.state.selection

    const marks = $from.marks()

    if (!marks.length) {
      this.editor.state.tr.setMeta(UNSET_ACTIVE_THREAD_META_KEY, true)
      this.editor.view.dispatch(this.editor.state.tr)
      return
    }

    const commentMark = this.editor.schema.marks.comment
    const activeCommentMark = marks.find((mark) => mark.type === commentMark)

    if (activeCommentMark) {
      const tr = this.editor.state.tr
      tr.setMeta(SET_ACTIVE_THREAD_META_KEY, activeCommentMark.attrs.id)
      this.editor.view.dispatch(tr)
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
        getAttrs: (el) => {
          const id = (el as HTMLSpanElement).getAttribute('data-comment-id')
          return id?.trim() ? { id } : null
        },
      },
      {
        tag: 'span[data-comment-resolved]',
        getAttrs: (el) => {
          const resolved = (el as HTMLSpanElement).getAttribute(
            'data-comment-resolved',
          )
          return {
            resolved: resolved === 'true',
            class: this.options.classes.threadResolved,
          }
        },
      },
      {
        tag: 'span[data-comment-needs-review]',
        getAttrs: (el) => {
          const needsReview = (el as HTMLSpanElement).getAttribute(
            'data-comment-needs-review',
          )
          const isResolved = (el as HTMLSpanElement).getAttribute(
            'data-comment-resolved',
          )
          return {
            needsReview: needsReview === 'true',
            class:
              isResolved === 'true'
                ? this.options.classes.threadResolved
                : this.options.classes.threadNeedsReview,
          }
        },
      },
      {
        tag: 'span[data-comment-is-system-thread]',
        getAttrs: (el) => {
          const isSystemThread = (el as HTMLSpanElement).getAttribute(
            'data-comment-is-system-thread',
          )
          return {
            isSystem: isSystemThread === 'true',
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ]
  },
})

export const HiddenComment = Mark.create({
  name: 'comment',

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
        getAttrs: (el) => {
          const id = (el as HTMLSpanElement).getAttribute('data-comment-id')
          return id?.trim() ? { id } : null
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ]
  },
})
